// AHA-47349-presenter-can-create-new-slide-type
// Developer slide types: look up a user's manifest base link (per environment)
// from the Manifest URL API, fetch that base + /manifest.json, and return its
// slide-type declarations flattened into the same shape /api/slide-types uses,
// to be appended to the marketplace catalogue.
import Config from '@/config'

// When the Manifest URL API has no record for this user+env (or the call fails),
// fall back to the local dev server.
const DEFAULT_LINK = 'http://localhost:5173'

// The Manifest URL API is keyed by its own 5-env model (sandbox/local/staging/
// canary/production — the tabs in DeveloperDomains). Config.env (VUE_APP_ENVAPP)
// CANNOT distinguish them — it is `develop` on both sandbox and staging, and
// `production` on both canary and production — so, exactly like
// resolveMarketplaceBase, derive the API env from the DOMAIN the build was
// compiled with (presenter.dev.*/sandbox/... / presenter.dev.* / presenter2.* / ...).
// Keep this in sync with the audience app's copy so a creator's saved record is
// read back by both. Same resolver drives the fetch and the DeveloperDomains tab.
const LOCAL_HOSTS = ['localhost', '127.0.0.1']
export const resolveManifestEnvFromUrl = (domainUrl) => {
  let host = ''
  let path = ''
  try {
    const url = new URL(domainUrl)
    host = url.hostname.toLowerCase()
    path = url.pathname
  } catch (e) {
    host = ''
  }
  if (LOCAL_HOSTS.includes(host)) return 'local'
  // Presenter's sandbox is a path under the dev host; audience's is a `.sandbox.` subdomain.
  if (host.includes('.dev.') && path.startsWith('/sandbox/')) return 'sandbox'
  if (host.includes('.sandbox.')) return 'sandbox'
  if (host.includes('.dev.')) return 'staging'
  // Canary uses the "<app>2.ahaslides.<tld>" host variant (presenter2/audience2).
  if (/2\.ahaslides\.(com|eu)$/.test(host)) return 'canary'
  if (host) return 'production'
  // No parseable host (misconfig): fall back to Config.env, else local.
  return Config.env === 'production' ? 'production' : 'local'
}
export const resolveManifestEnv = () => resolveManifestEnvFromUrl(Config.presenterDomain)

const trimTrailingSlash = (value) => String(value || '').replace(/\/+$/, '')

// Bounded fetch: this runs on the cached picker-open fast path (awaited by every
// caller of fetchMarketplaceSlideTypes), so an unreachable/slow manifest host must
// never stall it indefinitely. Abort each request after a hard timeout; on abort
// fetch rejects and the callers below fall back to DEFAULT_LINK / [].
const FETCH_TIMEOUT_MS = 5000
const fetchWithTimeout = async (url, options) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = controller ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS) : null
  try {
    return await fetch(url, controller ? { ...options, signal: controller.signal } : options)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

// Make a (possibly relative) URL absolute against the manifest base link.
const absolutize = (base, url) => {
  const value = String(url || '')
  if (!value) return value
  if (/^https?:\/\//i.test(value)) return value
  return `${trimTrailingSlash(base)}${value.startsWith('/') ? '' : '/'}${value}`
}

// The manifest.json entries carry RELATIVE URLs (canvasUrl/audienceUrl/editorUrl/
// handlerUrl, icon, preview*). Turn every URL-ish key into a FULL link against the
// manifest base so canvas/editor iframes and icons load from the developer's own
// domain. A key is URL-ish if its name ends with "url" or is exactly "icon".
const isUrlKey = (key) => /url$/i.test(key) || key === 'icon'

const absolutizeUrlKeys = (cfg, base) => {
  const out = { ...cfg }
  Object.keys(out).forEach((key) => {
    if (isUrlKey(key) && typeof out[key] === 'string') {
      out[key] = absolutize(base, out[key])
    }
  })
  return out
}

// Flatten a manifest declaration the same way the marketplace feed does: spread
// ahaConfig, then the top-level entry, so name/pinKey/visible win. Then absolutize
// the MERGED result — not just ahaConfig — so every URL key becomes a full link
// whether it came from ahaConfig, from a flat entry (no ahaConfig), or a top-level
// override. (A flat entry or top-level URL key would otherwise stay relative and
// the editor iframe would resolve it against the presenter origin => blank canvas.)
const flatten = (entry, link) => {
  if (!entry) return null
  const cfg = entry.ahaConfig && typeof entry.ahaConfig === 'object' ? entry.ahaConfig : {}
  const { ahaConfig, ...topLevel } = entry
  return absolutizeUrlKeys({ ...cfg, ...topLevel }, link)
}

// Resolve the manifest base link for a user + env, or null when there is no
// record for this env / the call fails. NULL — not DEFAULT_LINK — on a miss: a
// localhost fallback here would fire `http://localhost:5173/manifest.json` from a
// deployed build (Chrome exempts localhost from mixed-content blocking), letting
// any local app inject slide types into a production catalogue. The localhost
// default belongs to the DeveloperDomains settings form (a seed value) and to
// LOCAL builds only — applied by the caller, never as a blanket runtime fallback.
const resolveManifestLink = async (userId, env) => {
  try {
    const url = `${Config.manifestApiBaseUrl}/api/manifest`
      + `?userId=${encodeURIComponent(userId)}&env=${encodeURIComponent(env)}`
    const res = await fetchWithTimeout(url, { headers: { accept: 'application/json' } })
    if (!res.ok) return null
    const data = await res.json()
    const records = (data && data.records) || []
    // Only accept a record for THIS env — no cross-env fallback.
    const record = records.find((item) => item && item.env === env)
    return (record && record.url) || null
  } catch (e) {
    return null
  }
}

/**
 * Returns the user's developer slide types for the current env, or [] when
 * there's nothing to add (no manifest.json, or a failed request).
 */
export const fetchDeveloperSlideTypes = async ({ userId, env = resolveManifestEnv() }) => {
  if (!userId || !env) return []

  let link = await resolveManifestLink(userId, env)
  // No record for this env: on a LOCAL build fall back to the local dev server so
  // a developer running locally works without first saving a record; on any
  // deployed build, a miss yields nothing (never fetch localhost from prod).
  if (!link) {
    if (env !== 'local') return []
    link = DEFAULT_LINK
  }

  // Fetch <link>/manifest.json. If it doesn't exist, do nothing further.
  let manifest
  try {
    const res = await fetchWithTimeout(`${trimTrailingSlash(link)}/manifest.json`, {
      headers: { accept: 'application/json' },
    })
    if (!res.ok) return []
    manifest = await res.json()
  } catch (e) {
    return []
  }

  const entries = Array.isArray(manifest) ? manifest : (manifest && manifest.slideTypes) || []
  return entries
    .map((entry) => flatten(entry, link))
    // Drop entries with no string `type` (every downstream lookup keys on `type`);
    // matches the audience normalizer's filter.
    .filter((t) => t && typeof t.type === 'string')
    // Stable marker so the embedApp `customSlideTypeByType` getter can resolve
    // these (they live in the slideMarketplace catalogue but are NOT
    // `marketplace/`-typed) without relying on a manifest-authored `source`.
    .map((slide) => ({ ...slide, isDeveloperSlideType: true }))
}

export default { fetchDeveloperSlideTypes }
